import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom, Subscription } from 'rxjs';
import { MessageService } from 'primeng/api';
import { Roles } from '../models/roles';
import { runtimeEnv } from '../config/runtime-env';
import {
  COMPLIANCE_STATUS_APPROVED,
  COMPLIANCE_STATUS_PENDING,
  getComplianceBlockedMessage,
  normalizeComplianceStatus,
} from '../utils/compliance';
import { HttpService } from './http.service';
import { LoggerService } from './logger.service';
import { AuthTokenResult, AuthTokenService } from './auth-token.service';

/** Intervalo para detectar cambios de cumplimiento vía webhook (Sumsub) sin recargar la página. */
const COMPLIANCE_POLL_INTERVAL_MS = 10_000;
const COGNITO_IDP_TARGET_PREFIX = 'AWSCognitoIdentityProviderService';

type CognitoError = {
  code?: string;
  message?: string;
};

@Injectable()
export class AuthService {
  user: any;
  role: any;
  logOutSubscription: Subscription | undefined;
  forceChangePasswordRequired = false;
  forceChangePasswordPromptVisible = false;
  forceChangePasswordFormVisible = false;
  private compliancePollTimer: ReturnType<typeof setInterval> | null = null;
  private loginPasswordForForceChange: string | null = null;
  private forceChangePasswordResolver: (() => void) | null = null;

  constructor(
    private router: Router,
    private http: HttpService,
    private authTokens: AuthTokenService,
    private logger: LoggerService,
    private messageService: MessageService,
    private ngZone: NgZone,
  ) {
    this.logOutSubscription = this.http.logOutSource.subscribe((resp) => {
      if (resp) {
        this.logOut();
      }
    });
  }

  logOut() {
    this.stopComplianceStatusPolling();
    this.clearLoginPasswordForForceChange();
    this.resetForceChangePasswordUi();
    this.authTokens.clearTokens();
    this.user = null;
    this.role = null;
    this.router.navigate(['/auth']);
  }

  logIn(username: string, password: string) {
    return new Promise(async (resolve) => {
      if (this.isCognitoConfigured()) {
        const cognitoLogin = await this.logInWithCognito(username, password);
        if (cognitoLogin) {
          this.setLoginPasswordForForceChange(password);
          resolve(true);
          return;
        }
      }

      this.http.post('auth/login', { username, password }).subscribe(
        (resp: any) => {
          this.authTokens.persistAuthTokens({
            accessToken: resp.access_token,
          });
          this.setLoginPasswordForForceChange(password);
          resolve(true);
        },
        () => {
          resolve(false);
        },
      );
    });
  }

  changePassword(currentPassword: string, newPassword: string) {
    return new Promise(async (resolve, reject) => {
      if (this.isCognitoConfigured() && this.isCurrentTokenFromCognito()) {
        try {
          await this.changeCognitoPassword(currentPassword, newPassword);
          await this.clearLegacyPasswordHashAfterCognitoChange();
          resolve({ status: 'Ok', message: 'Password updated successfully.' });
          return;
        } catch (error) {
          reject(this.normalizeCognitoError(error));
          return;
        }
      }

      this.http
        .post('auth/change-password', {
          current_password: currentPassword,
          new_password: newPassword,
        })
        .subscribe(
          (resp: any) => resolve(resp),
          (error) => reject(error)
        );
    });
  }

  private async clearLegacyPasswordHashAfterCognitoChange(): Promise<void> {
    try {
      await firstValueFrom(this.http.post('auth/legacy-password/clear', {}));
    } catch (error) {
      this.logger.log('Could not clear legacy password hash after Cognito password change:', error);
    }
  }

  private isCognitoConfigured(): boolean {
    return Boolean(
      runtimeEnv.cognitoRegion &&
        runtimeEnv.cognitoUserPoolId &&
        runtimeEnv.cognitoClientId,
    );
  }

  private cognitoEndpoint(): string {
    return `https://cognito-idp.${runtimeEnv.cognitoRegion}.amazonaws.com/`;
  }

  private platformUsernameToCognitoUsername(username: string): string {
    const local = (username || '').trim().toLowerCase();
    return `${local}@${runtimeEnv.cognitoLoginEmailDomain}`;
  }

  private async logInWithCognito(
    username: string,
    password: string,
  ): Promise<boolean> {
    try {
      const authResult = await this.initiateCognitoAuth(username, password);
      this.authTokens.persistAuthTokens(authResult);
      return true;
    } catch (error) {
      const normalized = this.normalizeCognitoError(error);
      if (!this.shouldTryLegacySync(normalized)) {
        return false;
      }

      const synced = await this.syncLegacyUserToCognito(username, password);
      if (!synced) {
        return false;
      }

      try {
        const authResult = await this.initiateCognitoAuth(username, password);
        this.authTokens.persistAuthTokens(authResult);
        return true;
      } catch {
        return false;
      }
    }
  }

  private async initiateCognitoAuth(
    username: string,
    password: string,
  ): Promise<AuthTokenResult> {
    const response = await fetch(this.cognitoEndpoint(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': `${COGNITO_IDP_TARGET_PREFIX}.InitiateAuth`,
      },
      body: JSON.stringify({
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: runtimeEnv.cognitoClientId,
        AuthParameters: {
          USERNAME: this.platformUsernameToCognitoUsername(username),
          PASSWORD: password,
        },
      }),
    });

    const body = await this.parseJsonResponse(response);
    if (!response.ok) {
      throw this.toCognitoError(body);
    }

    if (body?.ChallengeName) {
      throw {
        code: body.ChallengeName,
        message: 'Cognito auth challenge is not supported by this login form.',
      };
    }

    const result = body?.AuthenticationResult;
    if (!result?.AccessToken) {
      throw {
        code: 'MissingAccessToken',
        message: 'Cognito did not return an access token.',
      };
    }

    return {
      accessToken: result.AccessToken,
      refreshToken: result.RefreshToken,
      idToken: result.IdToken,
    };
  }

  private async changeCognitoPassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<void> {
    const accessToken = await this.authTokens.getValidAccessToken();
    if (!accessToken) {
      throw { code: 'NotAuthorizedException', message: 'Missing access token.' };
    }

    const response = await fetch(this.cognitoEndpoint(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': `${COGNITO_IDP_TARGET_PREFIX}.ChangePassword`,
      },
      body: JSON.stringify({
        AccessToken: accessToken,
        PreviousPassword: currentPassword,
        ProposedPassword: newPassword,
      }),
    });

    const body = await this.parseJsonResponse(response);
    if (!response.ok) {
      throw this.toCognitoError(body);
    }
  }

  private async syncLegacyUserToCognito(
    username: string,
    password: string,
  ): Promise<boolean> {
    try {
      const response = await fetch(`${this.http.basePath}/auth/sync-cognito`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  private shouldTryLegacySync(error: CognitoError): boolean {
    return ['UserNotFoundException', 'NotAuthorizedException'].includes(
      error.code || '',
    );
  }

  private async parseJsonResponse(response: Response): Promise<any> {
    try {
      return await response.json();
    } catch {
      return {};
    }
  }

  private toCognitoError(body: any): CognitoError {
    const rawCode = body?.__type || body?.code || body?.Code || '';
    const code = String(rawCode).split('#').pop();
    return {
      code,
      message: body?.message || body?.Message || 'Cognito request failed.',
    };
  }

  private normalizeCognitoError(error: any): CognitoError {
    if (error?.code || error?.message) {
      return error;
    }
    return {
      code: 'CognitoError',
      message: 'Cognito request failed.',
    };
  }

  private isCurrentTokenFromCognito(): boolean {
    return this.authTokens.isCurrentTokenFromCognito();
  }

  getUser() {
    return new Promise((resolve, reject) => {
      this.http.get('user/profile').subscribe(
        (resp: any) => {
          this.applyUserProfile(resp?.data);
          this.logger.log('User:', this.user);
          this.logger.log('Role:', this.role);
          resolve(resp);
        },
        (error) => {
          console.error('Error retrieving user:', error);
          reject(error);
        }
      );
    });
  }

  private applyUserProfile(profile: any): void {
    this.user = profile;
    if (this.user?.metadata && typeof this.user.metadata === 'string') {
      try {
        this.user.metadata = JSON.parse(this.user.metadata);
      } catch {
        this.user.metadata = null;
      }
    }
    if (this.user?.configuration && typeof this.user.configuration === 'string') {
      try {
        this.user.configuration = JSON.parse(this.user.configuration);
      } catch {
        this.user.configuration = null;
      }
    }
    this.role = this.user?.role?.role_id;
    this.forceChangePasswordRequired = Boolean(this.user?.force_change_password);
  }

  requiresForceChangePassword(): boolean {
    return this.forceChangePasswordRequired;
  }

  setLoginPasswordForForceChange(password: string): void {
    this.loginPasswordForForceChange = password;
  }

  hasLoginPasswordForForceChange(): boolean {
    return Boolean(this.loginPasswordForForceChange);
  }

  getLoginPasswordForForceChange(): string | null {
    return this.loginPasswordForForceChange;
  }

  clearLoginPasswordForForceChange(): void {
    this.loginPasswordForForceChange = null;
  }

  showForceChangePasswordForm(): void {
    this.forceChangePasswordPromptVisible = false;
    this.forceChangePasswordFormVisible = true;
  }

  openForceChangePasswordFlow(): Promise<void> {
    if (!this.forceChangePasswordRequired) {
      return Promise.resolve();
    }

    this.forceChangePasswordPromptVisible = true;
    this.forceChangePasswordFormVisible = false;

    return new Promise((resolve) => {
      this.forceChangePasswordResolver = resolve;
    });
  }

  dismissForceChangePassword(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http.post('auth/dismiss-force-change-password', {}).subscribe(
        () => {
          this.user = {
            ...this.user,
            force_change_password: false,
          };
          this.completeForceChangePasswordFlow();
          resolve();
        },
        (error) => reject(error),
      );
    });
  }

  submitForceChangePassword(
    newPassword: string,
    currentPassword: string,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http
        .post('auth/force-change-password', {
          current_password: currentPassword,
          new_password: newPassword,
        })
        .subscribe(
          () => {
            this.user = {
              ...this.user,
              force_change_password: false,
            };
            this.completeForceChangePasswordFlow();
            resolve();
          },
          (error) => reject(error),
        );
    });
  }

  private completeForceChangePasswordFlow(): void {
    this.forceChangePasswordRequired = false;
    this.resetForceChangePasswordUi();
    this.clearLoginPasswordForForceChange();
    this.forceChangePasswordResolver?.();
    this.forceChangePasswordResolver = null;
  }

  private resetForceChangePasswordUi(): void {
    this.forceChangePasswordPromptVisible = false;
    this.forceChangePasswordFormVisible = false;
  }

  /**
   * Consulta el perfil periódicamente para reflejar webhooks de verificación (p. ej. Sumsub rejected).
   */
  startComplianceStatusPolling(intervalMs = COMPLIANCE_POLL_INTERVAL_MS): void {
    this.stopComplianceStatusPolling();
    if (!this.shouldPollComplianceStatus()) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.compliancePollTimer = setInterval(() => {
        this.ngZone.run(() => {
          void this.pollComplianceStatusOnce();
        });
      }, intervalMs);
    });
  }

  stopComplianceStatusPolling(): void {
    if (this.compliancePollTimer !== null) {
      clearInterval(this.compliancePollTimer);
      this.compliancePollTimer = null;
    }
  }

  private async pollComplianceStatusOnce(): Promise<void> {
    if (!this.shouldPollComplianceStatus()) {
      this.stopComplianceStatusPolling();
      return;
    }

    const previous = this.getComplianceStatus();
    try {
      await this.getUser();
    } catch {
      return;
    }

    const current = this.getComplianceStatus();
    if (current !== COMPLIANCE_STATUS_PENDING) {
      this.stopComplianceStatusPolling();
    }

    if (previous === current) {
      return;
    }

    if (
      previous === COMPLIANCE_STATUS_APPROVED &&
      current !== COMPLIANCE_STATUS_APPROVED
    ) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cuenta restringida',
        detail: this.getComplianceBlockedMessage(),
        life: 10_000,
      });
      return;
    }

    if (
      previous !== COMPLIANCE_STATUS_APPROVED &&
      current === COMPLIANCE_STATUS_APPROVED
    ) {
      this.messageService.add({
        severity: 'success',
        summary: 'Cuenta aprobada',
        detail: 'Tu verificación fue aprobada. Ya puedes operar en la plataforma.',
        life: 8_000,
      });
    }
  }

  hasRole(requiredRole: Roles): boolean {
    return this.role === requiredRole;
  }

  isWhiteLabel(): boolean {
    return Boolean(this.user?.white_label);
  }

  isRelianceEnabled(): boolean {
    return Boolean(
      this.user?.effective_reliance_enabled ||
        this.user?.reliance_enabled ||
        this.user?.parent_reliance_enabled,
    );
  }

  isComplianceOperationExempt(): boolean {
    return Boolean(
      this.user?.effective_white_label ||
        this.isWhiteLabel() ||
        this.user?.parent_white_label ||
        this.user?.white_label_tenant ||
        this.isRelianceEnabled(),
    );
  }

  getCardBrand(card?: any): string {
    const fromCard = String(card?.card_brand || card?.provider_account || '')
      .trim()
      .toLowerCase();
    if (fromCard) {
      return fromCard;
    }
    return String(this.user?.card_brand || 'default').trim().toLowerCase() || 'default';
  }

  canViewPlatformServices(): boolean {
    return (
      this.hasRole(Roles.Admin) ||
      this.isWhiteLabel() ||
      this.hasProfilePlatformService('platform-services:visualize') ||
      this.hasProfilePlatformService('platform-services:manage')
    );
  }

  canOperatePlatformServices(): boolean {
    return (
      this.hasRole(Roles.Admin) ||
      this.isWhiteLabel() ||
      this.hasProfilePlatformService('platform-services:manage')
    );
  }

  canViewWhiteLabelRoles(): boolean {
    return this.hasRole(Roles.Admin) || this.isWhiteLabel();
  }

  canViewUsers(): boolean {
    return (
      this.hasRole(Roles.Admin) ||
      this.isWhiteLabel() ||
      this.hasProfilePlatformService('user:visualize') ||
      this.hasProfilePlatformService('user:create') ||
      this.hasProfilePlatformService('user:manage') ||
      this.hasProfilePlatformService('user:password:reset')
    );
  }

  canViewUserFees(): boolean {
    return (
      this.hasRole(Roles.Admin) ||
      this.hasProfilePlatformService('fees:visualize') ||
      this.hasProfilePlatformService('fees:manage')
    );
  }

  canEditUserFees(): boolean {
    return this.hasRole(Roles.Admin) || this.hasProfilePlatformService('fees:manage');
  }

  private hasProfilePlatformService(requiredCode: string): boolean {
    const permissions = this.user?.white_label_tenant_role_permissions;
    if (!Array.isArray(permissions)) {
      return false;
    }

    return permissions.some((permission: any) => {
      const code =
        permission?.platform_service?.code ??
        permission?.platform_service_code ??
        permission?.code ??
        permission?.tenant_permission_code;
      const permissionActive = permission?.active;
      const serviceActive = permission?.platform_service?.active;

      return (
        code === requiredCode &&
        permissionActive !== false &&
        serviceActive !== false
      );
    });
  }

  getComplianceStatus(): string {
    return normalizeComplianceStatus(this.user?.status_compliance);
  }

  isComplianceApproved(): boolean {
    return this.getComplianceStatus() === COMPLIANCE_STATUS_APPROVED;
  }

  isRelianceOnboardingRequired(): boolean {
    const inferredRequired =
      this.isRelianceEnabled() &&
      !Boolean(this.user?.has_reliance_signup) &&
      !this.isComplianceApproved();

    return Boolean(this.user?.reliance_onboarding_required) || inferredRequired;
  }

  private shouldPollComplianceStatus(): boolean {
    return (
      this.authTokens.hasAccessToken() &&
      !this.hasRole(Roles.Admin) &&
      this.getComplianceStatus() === COMPLIANCE_STATUS_PENDING
    );
  }

  /** Admin, white label y reliance pueden operar sin compliance approved. */
  canPerformTransactionalActions(): boolean {
    if (this.hasRole(Roles.Admin) || this.isComplianceOperationExempt()) {
      return true;
    }
    return this.isComplianceApproved();
  }

  getComplianceBlockedMessage(): string {
    return getComplianceBlockedMessage(this.user?.status_compliance);
  }

  /**
   * Devuelve false si la acción transaccional debe bloquearse (muestra toast).
   * Pasar open=false para cierres de modales (siempre permitido).
   */
  guardTransactionalAction(open = true): boolean {
    if (!open || this.canPerformTransactionalActions()) {
      return true;
    }
    this.messageService.add({
      severity: 'warn',
      summary: 'Cuenta no aprobada',
      detail: this.getComplianceBlockedMessage(),
      life: 8000,
    });
    return false;
  }
}
